import type { RouterHistory } from '@tanstack/react-router';
import { createRootRoute, createRoute, createRouter } from '@tanstack/react-router';
import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { DeleteAccountRoute, MePrivacyRoute, PrivacyRoute } from './bp03';
import { TryOnRoute } from './bp21';
import {
  BookConfirmRoute,
  BookServicesRoute,
  BookSlotRoute,
  BookTechnicianRoute,
  BookVerifyRoute,
  BookingDetailRoute,
  BookingLandingRoute,
  BookingLayout,
  MyBookingsRoute,
} from './bp04';
import { RatingRoute } from './bp05';
import { AdminLoginRoute, DeepLinkGuard, NotificationsRoute, UpdateRequiredRoute } from './bp11';
import { MyPhotoRoute, MyPhotosRoute } from './bp13';
import { MyWalletRoute } from './bp14';
import { ClientShell } from './ClientShell';

/**
 * BP-36 rule 5: a form or a page of prose keeps a readable column on a tablet or a desktop
 * instead of stretching to the window; lists and grids around it use the width instead.
 */
export function readable(Screen: () => JSX.Element): () => JSX.Element {
  return function Readable(): JSX.Element {
    return (
      <div data-testid="readable" className="mx-auto w-full max-w-xl">
        <Screen />
      </div>
    );
  };
}

/**
 * The client route tree: BP-01 §6's `/login` (now the one staff and admin sign-in) and BP-03 §6's
 * `/privacy` (public) and `/me/privacy`, plus BP-04 §6's booking journey — all inside the
 * 390 px `ClientShell`, and BP-05 §6's `/r/{token}` rating page.
 *
 * `/` is now BP-04's landing page. BP-00 §14 ruled the hello world "deleted in BP-01.T1"
 * and BP-01 left it standing because BP-00 §11's `hello.spec.ts` asserted it; §6 names
 * BP-04's "Landing, Book now" as the client index, so this is the BP that replaces both
 * the page and that journey (see `web/tests/e2e/booking.spec.ts`).
 */
export function createClientRouteTree() {
  const rootRoute = createRootRoute({
    component: ClientShell,
    notFoundComponent: ClientNotFound,
  });

  // BP-04 §6's client rows, under one pathless layout so the booking draft survives the
  // navigation between the five steps (see `bp04.tsx`'s `BookingLayout`). None is gated in
  // the browser: the journey is anonymous until the OTP step, and everything after it is
  // scoped to the session cookie server-side.
  const bookingLayout = createRoute({
    getParentRoute: () => rootRoute,
    id: 'booking',
    component: BookingLayout,
  });

  const bookingRoutes = (
    [
      ['/', readable(BookingLandingRoute)],
      ['/book/services', BookServicesRoute],
      ['/book/technician', BookTechnicianRoute],
      ['/book/slot', BookSlotRoute],
      ['/book/verify', readable(BookVerifyRoute)],
      ['/book/confirm', readable(BookConfirmRoute)],
      ['/booking/$bookingRef', BookingDetailRoute],
      ['/me/bookings', MyBookingsRoute],
    ] as const
  ).map(([path, component]) =>
    createRoute({ getParentRoute: () => bookingLayout, path, component }),
  );

  // `/login` is the one sign-in for staff and admins; the account's roles decide what
  // opens, and it leaves for `/staff/`. Customers sign in on the landing page, `/`, with
  // BP-11 R9's return path (`?next=`) — see `CustomerSignIn`.
  const loginRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/login',
    component: readable(AdminLoginRoute),
  });

  // BP-03 §6: `/privacy` is public (the notice needs no session) and `/me/privacy` is
  // §14.6's stub until BP-04 lands client login.
  const privacyRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/privacy',
    component: readable(PrivacyRoute),
  });

  const mePrivacyRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/me/privacy',
    component: readable(MePrivacyRoute),
  });

  // BP-11 R17: the public half of account deletion. Google Play wants a web
  // URL as well as the in-app path, one that answers for somebody who has
  // already uninstalled the app — so this route is outside every guard, like
  // `/privacy` above and for the same reason.
  const deleteAccountRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/delete-account',
    component: readable(DeleteAccountRoute),
  });

  // BP-05 §6: the rating link. Outside the booking layout — it is reached from a message,
  // not from the journey, and it shares no draft with it.
  const ratingRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/r/$token',
    component: readable(RatingRoute),
  });

  // BP-11 §6's four rows. Outside the booking layout: none of them shares
  // the draft, and `/b/$ref` only redirects into it.
  //
  // `/update-required` and `/try-on` are reachable without a session on
  // purpose — the first is shown to a build the api has refused, which by
  // definition could not sign in, and the second is what a store reviewer
  // opens before they have an account.
  // BP-13 §6's two rows. Outside the booking layout, like the rest of `/me`:
  // a gallery shares no draft with the booking journey. The static path is
  // declared before the parameterised one, the convention `/clients/new` set.
  const photoRoutes = (
    [
      ['/me/photos', MyPhotosRoute],
      ['/me/photos/$photoId', MyPhotoRoute],
      // BP-14 §6: the balance, what expires when, and the history. A client
      // reads; they never sell themselves anything (§2).
      ['/me/wallet', MyWalletRoute],
    ] as const
  ).map(([path, component]) => createRoute({ getParentRoute: () => rootRoute, path, component }));

  const bp11Routes = (
    [
      ['/settings/notifications', readable(NotificationsRoute)],
      ['/try-on', TryOnRoute],
      ['/update-required', readable(UpdateRequiredRoute)],
      ['/b/$ref', DeepLinkGuard],
      ['/b', DeepLinkGuard],
    ] as const
  ).map(([path, component]) => createRoute({ getParentRoute: () => rootRoute, path, component }));

  return rootRoute.addChildren([
    loginRoute,
    privacyRoute,
    mePrivacyRoute,
    deleteAccountRoute,
    ratingRoute,
    ...bp11Routes,
    ...photoRoutes,
    bookingLayout.addChildren(bookingRoutes),
  ]);
}

/** Anything outside the tree. */
export function ClientNotFound(): JSX.Element {
  const { t } = useTranslation('common');
  return (
    <p role="alert" data-testid="not-found">
      {t('route.notFound')}
    </p>
  );
}

/** Build the router. `history` is injected so tests can drive a memory history. */
export function createClientRouter(history: RouterHistory) {
  return createRouter({ routeTree: createClientRouteTree(), history });
}
